/**
 * DeptRoutesRegionsCollapsible — Master Addendum · Feb 2026.
 *
 * A click-to-populate "Routes & Regions" section that lives at the
 * bottom of every NECS department command center. Only Legal,
 * Customer Services, Sales & Business Development, and IT get full
 * edit access; every other department sees a read-only view.
 */
import { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { MapPin, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import AdminRoutesRegions from '../AdminRoutesRegions';
import { useAdminScopeVersion } from '../../lib/adminScopeTransport';

const FULL_ACCESS_DEPTS = new Set([
  'Legal & Compliance',
  'Customer Service',
  'Customer Services',
  'Sales & Business Development',
  'Information Technology',
  'IT',
]);

export default function DeptRoutesRegionsCollapsible({ deptLabel }) {
  const scopeVersion = useAdminScopeVersion();
  const readOnly = !FULL_ACCESS_DEPTS.has(deptLabel);
  // Full-access departments (Legal, CS, Sales, IT) see R&R expanded by
  // default so it functions as their primary map surface — no extra
  // click needed. Read-only departments keep the collapsed view so the
  // department card stays scanable.
  const [open, setOpen] = useState(false);
  const slug = deptLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return (
    <Card
      className="p-4 border border-[#C9A962]/40"
      style={{ backgroundColor: '#1a1a1a' }}
      data-testid={`dept-rr-collapsible-${slug}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid={`dept-rr-toggle-${slug}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-4 w-4 flex-shrink-0" style={{ color: '#C9A962' }} />
          <span
            className="text-sm font-bold uppercase tracking-widest text-white truncate"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Routes &amp; Regions · {deptLabel}
          </span>
          {readOnly && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ backgroundColor: '#2D2D2D', color: '#a0a0a0' }}
              data-testid={`dept-rr-readonly-badge-${slug}`}
            >
              <Lock className="h-3 w-3" /> Read-only
            </span>
          )}
        </div>
        <span className="text-zinc-400 text-xs flex items-center gap-1">
          {open ? 'Hide' : 'Open'}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {open && (
        <div className="mt-4" data-testid={`dept-rr-body-${slug}`}>
          <AdminRoutesRegions key={scopeVersion} deptLabel={deptLabel} readOnly={readOnly} />
        </div>
      )}
    </Card>
  );
}
